import { apiService } from './api';

export interface DeletionRestoreSettings {
  enabled: boolean;
  retentionDays: number;
}

export interface RestorableRecord {
  id: string;
  entityType: string;
  entityId: string;
  label: string;
  deletedAt: string;
  expiresAt: string;
  relatedCount: number;
  deletedBy?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface RestorableRecordsResponse {
  success: boolean;
  settings: DeletionRestoreSettings;
  data: RestorableRecord[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class DeletionRestoreService {
  async getSettings(): Promise<{ success: boolean; data: DeletionRestoreSettings }> {
    return apiService.get('/deletion-restore/settings');
  }

  async updateSettings(payload: Partial<DeletionRestoreSettings>): Promise<{ success: boolean; data: DeletionRestoreSettings }> {
    return apiService.put('/deletion-restore/settings', payload);
  }

  async getEntityTypes(): Promise<{ success: boolean; data: string[] }> {
    return apiService.get('/deletion-restore/entity-types');
  }

  async listRecords(params?: {
    page?: number;
    limit?: number;
    entityType?: string;
    search?: string;
  }): Promise<RestorableRecordsResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', String(params.page));
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.entityType) query.append('entityType', params.entityType);
    if (params?.search) query.append('search', params.search);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return apiService.get(`/deletion-restore${suffix}`);
  }

  async restore(id: string): Promise<{ success: boolean; message: string }> {
    return apiService.post(`/deletion-restore/${id}/restore`, {});
  }

  async permanentDelete(id: string): Promise<{ success: boolean; message: string }> {
    return apiService.post(`/deletion-restore/${id}/permanent-delete`, {});
  }

  async restoreBulk(ids: string[]): Promise<{
    success: boolean;
    message: string;
    data: {
      restored: number;
      failed: Array<{ id: string; error: string }>;
      results: Array<{ id: string; label: string; entityType: string }>;
    };
  }> {
    return apiService.post('/deletion-restore/bulk-restore', { ids });
  }

  async permanentDeleteBulk(ids: string[]): Promise<{
    success: boolean;
    message: string;
    data: {
      deleted: number;
      failed: Array<{ id: string; error: string }>;
      results: Array<{ id: string; label: string; entityType: string }>;
    };
  }> {
    return apiService.post('/deletion-restore/bulk-permanent-delete', { ids });
  }

  formatEntityType(entityType: string): string {
    const map: Record<string, string> = {
      Publisher: 'Publisher',
      GuestBlogSite: 'Guest Blog Site',
      Client: 'Client',
      Project: 'Project',
      User: 'User',
      GuestBlogPlacement: 'Placement',
      GuestBlogOrder: 'Order',
      Invoice: 'Invoice',
      InvoiceItem: 'Invoice Item',
      Category: 'Category',
      LiveLinkStatus: 'Live Link',
      PendingGuestBlogSite: 'Pending Site',
      ClientAssignment: 'Client Assignment',
      ClientPlan: 'Client Plan',
      ClientPlanSite: 'Plan Site',
      ProjectHistoricalUsage: 'Historical Usage',
      ClientGuestBlogSiteOverride: 'Price Override',
    };
    return map[entityType] || entityType;
  }

  formatUserName(user?: RestorableRecord['deletedBy']): string {
    if (!user) return 'Unknown';
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    return user.email;
  }

  daysRemaining(expiresAt: string): number {
    const ms = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }

  /** e.g. "30 days" / "1 day" for archive confirm copy */
  formatRetentionLabel(days: number): string {
    const n = Number.isFinite(days) && days > 0 ? Math.floor(days) : 30;
    return `${n} day${n === 1 ? '' : 's'}`;
  }
}

export const deletionRestoreService = new DeletionRestoreService();
